import React, { useState } from "react";
import { z } from "zod";
import { toast } from "react-toastify";
import TitlePage from "../components/section/TitlePage/Titlepage";
import BizStatusContent from "../components/section/BizStatus/BizStatusContent";
import DeliveryTrackingContent from "../components/section/DeliveryTracking/DeliveryTrackingContent";
import Button from "../components/ui/Button/Button";
import Modal from "../components/ui/Modal/Modal";
import FieldBox from "../utils/Form/ui/FieldBox";
import {
  sendEmail,
  sendSms,
  getTracking,
  getBizStatus,
} from "../api/test-service";

const inputClassName =
  "rounded-[4px] w-full text-[12px] px-2 py-1 focus:outline-none";

const textareaClassName =
  "w-full p-2 outline-none text-[12px] resize-none rounded-[4px]";

const ExternalServiceTestPage = () => {
  const [email, setEmail] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [phone, setPhone] = useState("");
  const [smsTitle, setSmsTitle] = useState("");
  const [smsMessage, setSmsMessage] = useState("");
  const [trackingNo, setTrackingNo] = useState("");
  const [trackingData, setTrackingData] = useState(null);
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [isTrackingLoading, setIsTrackingLoading] = useState(false);
  const [businessNo, setBusinessNo] = useState("");
  const [bizStatusData, setBizStatusData] = useState(null);
  const [isBizStatusModalOpen, setIsBizStatusModalOpen] = useState(false);
  const [isBizStatusLoading, setIsBizStatusLoading] = useState(false);

  const handleSubmitEmail = async (e) => {
    e.preventDefault();
    const sendEmailFormValue = z.object({
      email: z.email("이메일 형식이 올바르지 않습니다."),
      emailSubject: z.string().min(1, "제목을 입력해주세요."),
      emailBody: z.string().min(1, "내용을 입력해주세요."),
    });
    const isValid = sendEmailFormValue.safeParse({
      email,
      emailSubject,
      emailBody,
    });
    if (!isValid.success) {
      console.log(isValid.error);
      toast.error(`[메일 발송 테스트] ${isValid.error.issues[0].message}`);
      return;
    }

    const response = await sendEmail({
      toAddress: isValid.data.email,
      subject: isValid.data.emailSubject,
      body: isValid.data.emailBody,
    });

    if (response?.status === 200) {
      toast.success(`[메일 발송 테스트] 성공`);
    } else {
      toast.error(`[메일 발송 테스트] 실패`);
    }
  };

  const handleSubmitSms = async (e) => {
    e.preventDefault();
    const sendSmsFormValue = z.object({
      phone: z.string().min(1, "전화번호를 입력해주세요."),
      smsTitle: z.string().min(1, "제목을 입력해주세요."),
      smsMessage: z.string().min(1, "메시지를 입력해주세요."),
    });
    const isValid = sendSmsFormValue.safeParse({
      phone,
      smsTitle,
      smsMessage,
    });
    if (!isValid.success) {
      console.log(isValid.error);
      toast.error(`[SMS 발송 테스트] ${isValid.error.issues[0].message}`);
      return;
    }

    const response = await sendSms({
      toPhone: isValid.data.phone,
      title: isValid.data.smsTitle,
      content: isValid.data.smsMessage,
    });

    if (response?.status === 200) {
      toast.success(`[SMS 발송 테스트] 성공`);
    } else {
      toast.error(`[SMS 발송 테스트] 실패`);
    }
  };

  const handleSubmitTracking = async (e) => {
    e.preventDefault();
    const getTrackingFormValue = z.object({
      trackingNo: z.string().min(1, "운송장 번호를 입력해주세요."),
    });
    const isValid = getTrackingFormValue.safeParse({
      trackingNo,
    });
    if (!isValid.success) {
      console.log(isValid.error);
      toast.error(
        `[택배 운송장 조회 테스트] ${isValid.error.issues[0].message}`
      );
      return;
    }

    setIsTrackingLoading(true);
    setTrackingData(null);

    const response = await getTracking({
      trackingNo: isValid.data.trackingNo,
    });

    setIsTrackingLoading(false);

    if (response?.status === 200) {
      setTrackingData(response.data.data.result);
      setIsTrackingModalOpen(true);
      toast.success(`[택배 운송장 조회 테스트] 성공`);
    } else {
      toast.error(`[택배 운송장 조회 테스트] 실패`);
    }
  };

  const handleSubmitBusinessNo = async (e) => {
    e.preventDefault();
    const getBizStatusFormValue = z.object({
      bizNo: z.string().min(1, "사업자등록번호를 입력해주세요."),
    });
    const isValid = getBizStatusFormValue.safeParse({
      bizNo: businessNo,
    });
    if (!isValid.success) {
      console.log(isValid.error);
      toast.error(
        `[사업자등록번호 조회 테스트] ${isValid.error.issues[0].message}`
      );
      return;
    }

    setIsBizStatusLoading(true);
    setBizStatusData(null);

    const response = await getBizStatus({
      bizNo: isValid.data.bizNo,
    });

    setIsBizStatusLoading(false);

    if (response?.status === 200) {
      setBizStatusData(response.data?.data.result);
      setIsBizStatusModalOpen(true);
      toast.success(`[사업자등록번호 조회 테스트] 성공`);
    } else {
      toast.error(`[사업자등록번호 조회 테스트] 실패`);
    }
  };

  return (
    <div>
      <TitlePage title="외부서비스연동 테스트" />

      <div className="mt-4">
        <h2 className="text-[14px] font-bold mb-4">
          메일(AWS SES) 발송 테스트
        </h2>
        <form onSubmit={handleSubmitEmail}>
          <FieldBox label="이메일">
            <input
              type="text"
              className={inputClassName}
              placeholder="수신 이메일을 입력해주세요"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FieldBox>
          <FieldBox label="제목">
            <input
              type="text"
              className={inputClassName}
              placeholder="메일 제목을 입력해주세요"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
          </FieldBox>
          <FieldBox isLast label="내용" alignItems="items-start pt-6">
            <textarea
              className={textareaClassName}
              placeholder="메일 내용을 입력해주세요"
              rows={5}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
            />
          </FieldBox>
          <div className="flex justify-end mt-4">
            <Button
              type="submit"
              margin="m-0"
              width="w-24"
              primary
              text="발송"
            />
          </div>
        </form>
      </div>

      <div className="mt-10">
        <h2 className="text-[14px] font-bold mb-4">
          SMS(다이렉트센드) 발송 테스트
        </h2>
        <form onSubmit={handleSubmitSms}>
          <FieldBox label="전화번호">
            <input
              type="text"
              className={inputClassName}
              placeholder="수신 전화번호를 입력해주세요"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </FieldBox>
          <FieldBox label="제목">
            <input
              type="text"
              className={inputClassName}
              placeholder="SMS 제목을 입력해주세요"
              value={smsTitle}
              onChange={(e) => setSmsTitle(e.target.value)}
            />
          </FieldBox>
          <FieldBox isLast label="메시지" alignItems="items-start pt-6">
            <textarea
              className={textareaClassName}
              placeholder="SMS 메시지를 입력해주세요"
              rows={5}
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
            />
          </FieldBox>
          <div className="flex justify-end mt-4">
            <Button
              type="submit"
              margin="m-0"
              width="w-24"
              primary
              text="발송"
            />
          </div>
        </form>
      </div>

      <Modal
        title="배송추적 조회"
        maxWidth="max-w-[72rem]"
        width="w-[92vw]"
        height="h-[60vh]"
        padding="p-0"
        hasClose={true}
        hasCancel={false}
        hasSubmit={false}
        isOpenFromOutside={isTrackingModalOpen}
        setIsOpenFromOutside={setIsTrackingModalOpen}
        description={
          <div className="p-4">
            <DeliveryTrackingContent
              data={trackingData}
              trackingNo={trackingNo}
              isLoading={isTrackingLoading}
            />
          </div>
        }
      >
        <span className="hidden" />
      </Modal>

      <div className="mt-10">
        <h2 className="text-[14px] font-bold mb-4">
          택배(한진택배) 운송장 조회 테스트
        </h2>
        <form onSubmit={handleSubmitTracking}>
          <FieldBox isLast label="운송장 번호">
            <input
              type="text"
              className={inputClassName}
              placeholder="운송장 번호를 입력해주세요"
              value={trackingNo}
              onChange={(e) => setTrackingNo(e.target.value)}
            />
          </FieldBox>
          <div className="flex justify-end mt-4">
            <Button
              type="submit"
              margin="m-0"
              width="w-24"
              primary
              text="조회"
            />
          </div>
        </form>
      </div>

      <Modal
        title="사업자등록번호 조회"
        maxWidth="max-w-[72rem]"
        width="w-[92vw]"
        height="h-[60vh]"
        padding="p-0"
        hasClose={true}
        hasCancel={false}
        hasSubmit={false}
        isOpenFromOutside={isBizStatusModalOpen}
        setIsOpenFromOutside={setIsBizStatusModalOpen}
        description={
          <div className="p-4">
            <BizStatusContent
              data={bizStatusData}
              bizNo={businessNo}
              isLoading={isBizStatusLoading}
            />
          </div>
        }
      >
        <span className="hidden" />
      </Modal>

      <div className="mt-10">
        <h2 className="text-[14px] font-bold mb-4">
          사업자등록번호(공공데이터포털) 조회
        </h2>
        <form onSubmit={handleSubmitBusinessNo}>
          <FieldBox isLast label="사업자등록번호">
            <input
              type="text"
              className={inputClassName}
              placeholder="사업자등록번호를 입력해주세요"
              value={businessNo}
              onChange={(e) => setBusinessNo(e.target.value)}
            />
          </FieldBox>
          <div className="flex justify-end mt-4">
            <Button
              type="submit"
              margin="m-0"
              width="w-24"
              primary
              text="조회"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExternalServiceTestPage;
